import { z } from 'zod'
import { SHA256_HEX_PATTERN } from './version.js'

/**
 * The batch family — SM5 (B7.5, B7.3, B18.1; MCP-AC-122, DEC-06).
 *
 * One operation kind over an **explicit, bounded, frozen** item set, and three rules that are
 * shapes rather than promises:
 *
 * 1. **The set is explicit and bounded.** At most `MCP_BATCH_MAX_ITEMS` items, each naming its own
 *    id and the revision the caller read, and the schema refuses a duplicate id — so "the same item
 *    twice with two revisions" is not expressible, and neither is an unbounded sweep.
 * 2. **The set is bound by a digest.** `item_set_digest` is computed over the canonical frozen set
 *    and stored with the batch. A repeat prepare of the same `client_request_id` with a *different*
 *    set answers `REVISION_CONFLICT` rather than swapping the items under an approval that was
 *    recorded for the first set — that is what "no new unreviewed item inserted" means mechanically.
 * 3. **Nothing is sent unattended.** Each item is prepared as its own review on SM3's spine, so each
 *    item carries its own person-recorded approval; the batch commit consumes those approvals and
 *    performs nothing that a person did not approve item by item.
 *
 * The batch does not invent a second approval model. What it adds is the *set*: the frozen item list,
 * its digest, and a durable per-item result row — which is also what `signbento_get_job`'s `BATCH`
 * branch reads.
 */

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()
const Sha256Hex = z.string().regex(SHA256_HEX_PATTERN)

/**
 * The one operation kind this build batches. Both are families SM5 already serves as prepare/commit
 * pairs, so a batch item's action is the *same* action the single-item tool performs, with the same
 * planner and the same refusal codes — a batch is a composition, not a second implementation.
 *
 * `STATUS` is deliberately absent: a status batch is a read, `signbento_list_envelopes` already
 * answers it, and wrapping a read in an approval-shaped handle would invent an approval for
 * something that has none. `ARCHIVE` is absent for a different reason: it is a workspace item under
 * `workspace.write`, a different object model from the agreement an operational batch names, and one
 * tool carrying two item vocabularies would be a place for a caller to confuse them.
 */
export const MCP_BATCH_OPERATION_KINDS = ['REMINDER', 'VOID'] as const
export const McpBatchOperationKind = z.enum(MCP_BATCH_OPERATION_KINDS)
export type McpBatchOperationKind = z.infer<typeof McpBatchOperationKind>

/** The family's own name for each operation, so the review a person approves says which it is. */
export const MCP_BATCH_REVIEW_KINDS: Readonly<Record<McpBatchOperationKind, 'RESEND' | 'VOID'>> = {
  REMINDER: 'RESEND',
  VOID: 'VOID',
}

/** MCP-AC-122's bound, named once so the schema, the digest and the docs cannot drift. */
export const MCP_BATCH_MAX_ITEMS = 25

/**
 * One item: the agreement, the revision the caller read, and — for a reminder — the participant the
 * reminder is for. `revision` is the agreement's **operational** revision (`envelopes.lock_version`
 * plus one, the same positive-integer convention `mcp_reviews.source_revision` uses), which is the
 * value `signbento_get_envelope` reports; an item whose revision has moved is refused rather than
 * acted on.
 */
export const batchItemRef = z
  .object({
    item_id: Id,
    revision: z.number().int().nonnegative(),
    recipient_id: Id.nullable().optional(),
  })
  .strict()
export type BatchItemRef = z.infer<typeof batchItemRef>

/**
 * The prepare request. The refine is what makes "one operation kind" structural: a reminder names
 * the participant it is for, and a void must not — so an item cannot carry a target the operation
 * would silently ignore.
 */
export const prepareBatchInput = z
  .object({
    operation: McpBatchOperationKind,
    items: z.array(batchItemRef).min(1).max(MCP_BATCH_MAX_ITEMS),
    client_request_id: z.string().min(1).max(128),
  })
  .strict()
  .superRefine((input, ctx) => {
    const seen = new Set<string>()
    input.items.forEach((item, index) => {
      if (seen.has(item.item_id)) {
        ctx.addIssue({
          code: 'custom',
          message: 'an item appears once',
          path: ['items', index, 'item_id'],
        })
      }
      seen.add(item.item_id)
      const needsRecipient = input.operation === 'REMINDER'
      if (needsRecipient && (item.recipient_id ?? null) === null) {
        ctx.addIssue({
          code: 'custom',
          message: 'a reminder names the participant it is for',
          path: ['items', index, 'recipient_id'],
        })
      }
      if (!needsRecipient && (item.recipient_id ?? null) !== null) {
        ctx.addIssue({
          code: 'custom',
          message: 'this operation takes no participant',
          path: ['items', index, 'recipient_id'],
        })
      }
    })
  })
export type PrepareBatchInput = z.infer<typeof prepareBatchInput>

/**
 * The commit request. There is no batch-level approval token and no request key: the authority is
 * each item's own recorded approval, and the idempotency is each item's own stored commit — so a
 * repeat of this call is a *retry of what is left*, never a second run of what already happened.
 *
 * `approvals` is how each item's authority is proven: the token the person's approval issued for
 * that item's review, exactly as `signbento_get_review_status` handed it to this host. An item with no
 * token is not approved and is refused alone (`REVIEW_REQUIRED`), which is what makes a partial
 * batch a normal outcome rather than an error.
 */
export const batchItemApproval = z
  .object({ item_id: Id, review_token: z.string().min(1).max(512) })
  .strict()
export type BatchItemApproval = z.infer<typeof batchItemApproval>

export const commitBatchInput = z
  .object({
    batch_id: Id,
    approvals: z.array(batchItemApproval).max(MCP_BATCH_MAX_ITEMS).default([]),
  })
  .strict()
export type CommitBatchInput = z.infer<typeof commitBatchInput>

/**
 * The per-item lifecycle. `SUCCEEDED` is the only state a retry never re-attempts, which is exactly
 * MCP-AC-122's "completed actions not repeated on retry"; `REFUSED` and `FAILED` are both
 * re-attemptable because neither performed anything.
 */
export const MCP_BATCH_ITEM_STATES = ['PENDING', 'SUCCEEDED', 'REFUSED', 'FAILED'] as const
export const McpBatchItemState = z.enum(MCP_BATCH_ITEM_STATES)
export type McpBatchItemState = z.infer<typeof McpBatchItemState>

/** The batch's own lifecycle. Every state is a durable row's state, never a derived guess. */
export const MCP_BATCH_STATES = [
  'AWAITING_APPROVAL',
  'RUNNING',
  'SUCCEEDED',
  'PARTIAL',
  'REFUSED',
  'FAILED',
] as const
export const McpBatchState = z.enum(MCP_BATCH_STATES)
export type McpBatchState = z.infer<typeof McpBatchState>

/** Bounded counters only — an item count, never an item's content. */
export const McpBatchCounts = z
  .object({
    total: z.number().int().positive(),
    succeeded: z.number().int().nonnegative(),
    refused: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
  })
  .strict()
export type McpBatchCounts = z.infer<typeof McpBatchCounts>

/** One item as both results report it: the id, the frozen revision, the state and the code. */
export const McpBatchItemResult = z
  .object({
    item_id: Id,
    revision: z.number().int().nonnegative(),
    recipient_id: Id.nullable(),
    state: McpBatchItemState,
    /** The machine code that decided this item: a registry code, never prose. */
    outcome_code: z.string().min(1).max(120).nullable(),
    retryable: z.boolean().nullable(),
    /** The item's own review, when the prepare froze one. `null` when it refused before that. */
    review_id: Id.nullable(),
    /** What the item's own commit answered, verbatim. `null` until it succeeds. */
    result: z.record(z.string(), z.unknown()).nullable(),
  })
  .strict()
export type McpBatchItemResult = z.infer<typeof McpBatchItemResult>

/**
 * The prepare's answer. `prepared` counts the items that got a review; `refused` counts the ones
 * whose own planner refused them before a review existed. Both are reported per item, because
 * MCP-AC-122 asks for *per-item current validation* rather than one verdict for the set.
 */
export const McpBatchPrepareResult = z
  .object({
    batch_id: Id,
    operation: McpBatchOperationKind,
    state: McpBatchState,
    item_set_digest: Sha256Hex,
    counts: McpBatchCounts,
    items: z.array(McpBatchItemResult).max(MCP_BATCH_MAX_ITEMS),
    bounds: z
      .object({ max_items: z.number().int().positive(), item_count: z.number().int().positive() })
      .strict(),
    /** Where each item's approval is recorded: the trusted surface, one review per item. */
    next_step: z
      .object({
        kind: z.literal('APPROVE_ITEMS_IN_TRUSTED_SURFACE'),
        review_ids: z.array(Id).max(MCP_BATCH_MAX_ITEMS),
      })
      .strict(),
    expires_at: IsoDateTime,
    /** Literals: a prepare freezes and approves nothing and performs nothing. */
    performed: z.literal(false),
    idempotent_replay: z.boolean(),
  })
  .strict()
export type McpBatchPrepareResult = z.infer<typeof McpBatchPrepareResult>

/**
 * The commit's answer. `remaining` names the items a retry would attempt, so a caller does not have
 * to derive it from the item list — and `idempotent_replay` distinguishes "nothing was left to do"
 * from "this call did the work".
 */
export const McpBatchCommitResult = z
  .object({
    batch_id: Id,
    operation: McpBatchOperationKind,
    state: McpBatchState,
    item_set_digest: Sha256Hex,
    counts: McpBatchCounts,
    items: z.array(McpBatchItemResult).max(MCP_BATCH_MAX_ITEMS),
    /** The ids a retry re-attempts: every item that is not `SUCCEEDED`. */
    remaining: z.array(Id).max(MCP_BATCH_MAX_ITEMS),
    /** True when this call found nothing left to attempt and answered the stored state. */
    idempotent_replay: z.boolean(),
  })
  .strict()
export type McpBatchCommitResult = z.infer<typeof McpBatchCommitResult>

/**
 * The canonical item-set digest. It is computed from the *frozen* set — the operation and each item's
 * id, revision and participant in the order the caller named them — so two calls that name the same
 * set produce the same digest whatever order they list it in is not claimed: order is part of the
 * set, because the set's order is what the person was shown.
 */
export function canonicalItemSet(input: {
  readonly operation: McpBatchOperationKind
  readonly items: readonly BatchItemRef[]
}): string {
  const rows = input.items.map(
    (item) => `${item.item_id}\u0000${String(item.revision)}\u0000${item.recipient_id ?? ''}`,
  )
  return [input.operation, ...rows].join('\u0001')
}
